import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import { AuthService } from './auth.service';
import {
    IBondRegisterReport,
    IExpAgingReport,
    IExportReceivableReport,
    IIncentiveStatusReport,
    ILcPositionReport,
    IOrderProfitabilityReport,
} from '../interfaces/commercial.interface';

const headers = () => AuthService.getAuthHeader();

/**
 * T11 — the three read-only commercial reports (T8's backend). JSON reads use the plain axios
 * default responseType; XLSX downloads set `responseType: 'blob'` and return the raw Blob so the
 * caller can hand it to `triggerDownload` (statutoryRegisters/shared.ts) — no second object-URL
 * dance is hand-rolled here.
 */
export const CommercialReportService = {
    getLcPositionReport: async (institutionId: string): Promise<ILcPositionReport> =>
        (await axios.get(ENDPOINTS.COMMERCIAL_REPORT.LC_POSITION(institutionId), { headers: headers() })).data,

    downloadLcPositionReportXlsx: async (institutionId: string): Promise<Blob> =>
        (await axios.get(ENDPOINTS.COMMERCIAL_REPORT.LC_POSITION_XLSX(institutionId), {
            headers: headers(),
            responseType: 'blob',
        })).data,

    getUnrealizedExpAgingReport: async (institutionId: string): Promise<IExpAgingReport> =>
        (await axios.get(ENDPOINTS.COMMERCIAL_REPORT.UNREALIZED_EXP_AGING(institutionId), { headers: headers() })).data,

    downloadUnrealizedExpAgingReportXlsx: async (institutionId: string): Promise<Blob> =>
        (await axios.get(ENDPOINTS.COMMERCIAL_REPORT.UNREALIZED_EXP_AGING_XLSX(institutionId), {
            headers: headers(),
            responseType: 'blob',
        })).data,

    getExportReceivableReport: async (institutionId: string): Promise<IExportReceivableReport> =>
        (await axios.get(ENDPOINTS.COMMERCIAL_REPORT.EXPORT_RECEIVABLES(institutionId), { headers: headers() })).data,

    getIncentiveClaimStatusReport: async (institutionId: string): Promise<IIncentiveStatusReport> =>
        (await axios.get(ENDPOINTS.COMMERCIAL_REPORT.INCENTIVE_CLAIM_STATUS(institutionId), { headers: headers() })).data,

    downloadIncentiveClaimStatusReportXlsx: async (institutionId: string): Promise<Blob> =>
        (await axios.get(ENDPOINTS.COMMERCIAL_REPORT.INCENTIVE_CLAIM_STATUS_XLSX(institutionId), {
            headers: headers(),
            responseType: 'blob',
        })).data,

    // T8 (brief item 13.3) — bond register, keyed to a buyer order.
    getBondRegister: async (institutionId: string, buyerOrderId: string): Promise<IBondRegisterReport> =>
        (await axios.get(ENDPOINTS.COMMERCIAL_REPORT.BOND_REGISTER(institutionId, buyerOrderId), { headers: headers() })).data,

    downloadBondRegisterXlsx: async (institutionId: string, buyerOrderId: string): Promise<Blob> =>
        (await axios.get(ENDPOINTS.COMMERCIAL_REPORT.BOND_REGISTER_XLSX(institutionId, buyerOrderId), {
            headers: headers(),
            responseType: 'blob',
        })).data,

    // T6 (brief item 16.3) — order profitability, keyed to a buyer order; exchangeRate optional.
    getOrderProfitability: async (institutionId: string, buyerOrderId: string, exchangeRate?: number): Promise<IOrderProfitabilityReport> =>
        (await axios.get(ENDPOINTS.COMMERCIAL_REPORT.ORDER_PROFITABILITY(institutionId, buyerOrderId, exchangeRate), { headers: headers() })).data,

    downloadOrderProfitabilityXlsx: async (institutionId: string, buyerOrderId: string, exchangeRate?: number): Promise<Blob> =>
        (await axios.get(ENDPOINTS.COMMERCIAL_REPORT.ORDER_PROFITABILITY_XLSX(institutionId, buyerOrderId, exchangeRate), {
            headers: headers(),
            responseType: 'blob',
        })).data,
};
